import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import CartDebugInfo from 'components/CartDebugInfo';
import styles from 'components/CartDrawer/styles';
import GrowlerPricingBreakdown from 'components/GrowlerPricingBreakdown';
import { useGrowlersCartPricing } from 'hooks/useGrowlersCartPricing';
import { useGrowlersCashback } from 'hooks/useGrowlersCashback';
import { useCart } from 'services/cart/CartContext';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { formatCurrencyBRL, roundCurrency } from 'utils/currency';
import { isGrowlersPath } from 'utils/growlersFlow';

const CartDrawer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const beerCart = useCart();
  const growlerCart = useGrowlerCart();
  const isGrowlers = isGrowlersPath(location.pathname);

  const cart = isGrowlers ? growlerCart.cart : beerCart.cart;
  const isOpen = isGrowlers ? growlerCart.isOpen : beerCart.isOpen;
  const closeCart = isGrowlers ? growlerCart.closeCart : beerCart.closeCart;
  const clearCart = isGrowlers ? growlerCart.clearCart : beerCart.clearCart;
  const drawerWidth = isMobile ? '100%' : 400;
  const growlerPricing = useGrowlersCartPricing(
    growlerCart.cart.totalItems,
    growlerCart.cart.totalValue,
    isGrowlers,
  );
  const growlerMerchandiseSubtotal = roundCurrency(
    Math.max(0, growlerPricing.subtotal - growlerPricing.discount),
  );
  const growlerCashback = useGrowlersCashback(
    isGrowlers ? growlerMerchandiseSubtotal : 0,
  );

  const handleFinalizePedido = () => {
    closeCart();
    navigate(
      isGrowlers ? '/growlers/order-confirmation' : '/order-confirmation',
    );
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closeCart}
      sx={{
        ...styles.drawer,
        '& .MuiDrawer-paper': {
          ...styles.drawerPaper,
          width: drawerWidth,
        },
      }}
    >
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography variant="h6" sx={styles.title}>
            MEU CARRINHO
          </Typography>
          <IconButton onClick={closeCart} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={styles.content}>
          {cart.items.length === 0 ? (
            <Box sx={styles.emptyCart}>
              <ShoppingCartCheckoutIcon sx={styles.emptyIcon} />
              <Typography variant="h6" sx={styles.emptyTitle}>
                Seu carrinho está vazio
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={styles.emptySubtitle}
              >
                {isGrowlers
                  ? 'Adicione growlers ou garrafas para começar!'
                  : 'Adicione algumas cervejas para começar!'}
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={styles.itemsList}>
                {isGrowlers
                  ? growlerCart.cart.items.map(item => (
                      <ListItem key={item.id} sx={styles.cartItem}>
                        <Box sx={styles.itemContainer}>
                          <Box sx={styles.imageContainer}>
                            <img
                              src={item.growler.image}
                              alt={item.growler.name}
                              style={styles.productImage}
                            />
                          </Box>
                          <Box sx={styles.productInfo}>
                            <Typography
                              variant="subtitle2"
                              sx={styles.productName}
                            >
                              {item.growler.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={styles.productBrewery}
                            >
                              {item.growler.brewery}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={styles.productPrice}
                            >
                              R$ {item.growler.price.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={styles.quantityControls}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                growlerCart.updateQuantity(
                                  item.growler.id,
                                  item.quantity - 1,
                                )
                              }
                              disabled={item.quantity <= 1}
                              sx={styles.quantityButton}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={styles.quantity}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                growlerCart.updateQuantity(
                                  item.growler.id,
                                  item.quantity + 1,
                                )
                              }
                              sx={styles.quantityButton}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              growlerCart.removeFromCart(item.growler.id)
                            }
                            sx={styles.removeButton}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={styles.itemTotal}>
                          <Typography variant="body2" sx={styles.itemTotalText}>
                            Subtotal: R${' '}
                            {(
                              Math.round(
                                item.growler.price * item.quantity * 100,
                              ) / 100
                            ).toFixed(2)}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))
                  : beerCart.cart.items.map(item => (
                      <ListItem key={item.id} sx={styles.cartItem}>
                        <Box sx={styles.itemContainer}>
                          <Box sx={styles.imageContainer}>
                            <img
                              src={item.beer.image}
                              alt={item.beer.name}
                              style={styles.productImage}
                            />
                          </Box>
                          <Box sx={styles.productInfo}>
                            <Typography
                              variant="subtitle2"
                              sx={styles.productName}
                            >
                              {item.beer.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={styles.productBrewery}
                            >
                              {item.beer.brewery}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={styles.productPrice}
                            >
                              R$ {item.beer.price.toFixed(2)}
                            </Typography>
                            {item.beer.subscriberPrice !== item.beer.price && (
                              <Typography
                                variant="caption"
                                sx={styles.subscriberPrice}
                              >
                                Assinante: R${' '}
                                {item.beer.subscriberPrice.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={styles.quantityControls}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                beerCart.updateQuantity(
                                  item.beer.id,
                                  item.quantity - 1,
                                )
                              }
                              disabled={item.quantity <= 1}
                              sx={styles.quantityButton}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={styles.quantity}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                beerCart.updateQuantity(
                                  item.beer.id,
                                  item.quantity + 1,
                                )
                              }
                              sx={styles.quantityButton}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              beerCart.removeFromCart(item.beer.id)
                            }
                            sx={styles.removeButton}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={styles.itemTotal}>
                          <Typography variant="body2" sx={styles.itemTotalText}>
                            Subtotal: R${' '}
                            {(
                              Math.round(
                                item.beer.price * item.quantity * 100,
                              ) / 100
                            ).toFixed(2)}
                          </Typography>
                          {item.beer.subscriberPrice !== item.beer.price && (
                            <Typography
                              variant="caption"
                              sx={styles.itemSubscriberTotal}
                            >
                              Assinante: R${' '}
                              {(
                                Math.round(
                                  item.beer.subscriberPrice *
                                    item.quantity *
                                    100,
                                ) / 100
                              ).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                    ))}
              </List>

              <Divider />

              <Box sx={styles.summary}>
                <Box sx={styles.summaryRow}>
                  <Typography variant="body2">Total de itens:</Typography>
                  <Typography variant="body2" sx={styles.summaryValue}>
                    {cart.totalItems} {cart.totalItems === 1 ? 'item' : 'itens'}
                  </Typography>
                </Box>

                {isGrowlers ? (
                  <>
                    <GrowlerPricingBreakdown
                      pricing={growlerPricing}
                      rowSx={styles.summaryRow}
                      totalLabelSx={styles.totalLabel}
                      totalValueSx={styles.totalValue}
                      hintSx={styles.savings}
                    />
                    {growlerCashback.earnAmount > 0 && (
                      <Box sx={styles.cashbackBanner}>
                        <Typography sx={styles.cashbackBannerLabel}>
                          Cashback nesta compra
                        </Typography>
                        <Typography sx={styles.cashbackBannerAmount}>
                          R$ {formatCurrencyBRL(growlerCashback.earnAmount)}
                        </Typography>
                        <Typography sx={styles.cashbackBannerHint}>
                          Você ganha{' '}
                          {growlerCashback.earnPercent > 0
                            ? `${growlerCashback.earnPercent}% `
                            : ''}
                          de volta sobre o valor dos itens
                        </Typography>
                      </Box>
                    )}
                    {growlerCashback.balance > 0 && (
                      <Box sx={styles.cashbackBalanceBanner}>
                        <Typography sx={styles.cashbackBalanceLabel}>
                          Seu saldo de cashback
                        </Typography>
                        <Typography sx={styles.cashbackBalanceAmount}>
                          R$ {formatCurrencyBRL(growlerCashback.balance)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={styles.summaryRow}>
                    <Typography variant="body1" sx={styles.totalLabel}>
                      Total:
                    </Typography>
                    <Typography variant="h6" sx={styles.totalValue}>
                      R$ {cart.totalValue.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {!isGrowlers &&
                  beerCart.cart.subscriberTotalValue !==
                    beerCart.cart.totalValue && (
                    <>
                      <Box sx={styles.summaryRow}>
                        <Typography
                          variant="body2"
                          sx={styles.subscriberTotalLabel}
                        >
                          Total Assinante:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={styles.subscriberTotalValue}
                        >
                          R$ {beerCart.cart.subscriberTotalValue.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={styles.savingsContainer}>
                        <Typography variant="caption" sx={styles.savings}>
                          Economia sendo assinante: R${' '}
                          {(
                            beerCart.cart.totalValue -
                            beerCart.cart.subscriberTotalValue
                          ).toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
              </Box>
            </>
          )}
        </Box>

        {cart.items.length > 0 && (
          <Box sx={styles.footer}>
            <Button
              variant="outlined"
              onClick={clearCart}
              sx={styles.clearButton}
              fullWidth
            >
              Limpar Carrinho
            </Button>
            <Button
              variant="contained"
              sx={styles.checkoutButton}
              fullWidth
              onClick={handleFinalizePedido}
            >
              Finalizar Pedido
            </Button>
            {!isGrowlers && (
              <Box sx={styles.persistenceIndicator}>
                <CartDebugInfo showDetails={false} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
